import React, { useRef, useState } from "react";
import { Modal, Button, Spin, Tooltip } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { FaRegFileWord, FaRegFilePdf } from "react-icons/fa";
import { exportReportToPdf, exportReportToWord } from "./reportExport";

// Reusable shell for every home report: coloured header (icon + title + meta),
// a captured content area (the report body), and a footer with Close + the two
// download buttons. Export reads the live DOM of the body, so charts and KPI
// tiles are always in sync with what's on screen.
const ReportModal = ({
  open,
  onClose,
  title,
  meta,
  icon,
  color = "#aa1a5f",
  loading = false,
  kpis = [],
  children,
}) => {
  const contentRef = useRef(null);
  const [busy, setBusy] = useState(null); // "pdf" | "word" | null

  const runExport = async (kind) => {
    if (loading || !contentRef.current) return;
    setBusy(kind);
    try {
      const args = { title, meta, kpis };
      if (kind === "pdf") await exportReportToPdf(contentRef.current, args);
      else await exportReportToWord(contentRef.current, args);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Report ${kind} export failed:`, err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={1120}
      centered
      destroyOnClose
      className="reportModal"
      styles={{ body: { padding: 0 } }}
    >
      {/* White header with dark text: the report's own numbers should be the
          only strong thing on screen. The report's colour survives as the icon
          and the hairline under the header. */}
      <div className="reportModalHeader">
        <div className="reportModalHeaderMain">
          <span className="reportModalIcon" style={{ color }}>
            {icon}
          </span>
          <div>
            <h2 className="reportModalTitle">{title}</h2>
            {meta ? <p className="reportModalMeta">{meta}</p> : null}
          </div>
        </div>
        <Button
          type="text"
          className="reportModalClose"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Close report"
        />
      </div>

      <div className="reportModalScroll">
        {/* The body must stay mounted (and visible) even while loading — it's
            what runs the data fetch and reports back via onLoadingChange. It
            renders null until its data is ready, so the overlaid spinner is all
            the user sees during load. Keeping the container visible avoids
            chart.js canvases mounting at 0×0 inside a hidden node. */}
        <div ref={contentRef} className="reportExportArea">
          {children}
        </div>
        {loading && (
          <div className="reportModalLoading">
            <Spin />
          </div>
        )}
      </div>

      <div className="reportModalFooter">
        <Button className="reportBtnClose" onClick={onClose}>
          Close
        </Button>
        <div className="reportFooterExports">
          <Tooltip title="Download as Word document">
            <Button
              className="reportBtnWord"
              icon={<FaRegFileWord />}
              loading={busy === "word"}
              disabled={loading || busy === "pdf"}
              onClick={() => runExport("word")}
            >
              Word
            </Button>
          </Tooltip>
          <Tooltip title="Download as PDF">
            <Button
              className="reportBtnPdf"
              icon={<FaRegFilePdf />}
              loading={busy === "pdf"}
              disabled={loading || busy === "word"}
              onClick={() => runExport("pdf")}
            >
              PDF
            </Button>
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
};

export default ReportModal;
